import React, { useState, useEffect, useCallback } from 'react';
import { useFirmMode } from '../contexts/FirmModeContext';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import './FirmCasesPage.css';

const FirmCasesPage = () => {
  const { getFirmToken, clearFirmMode, setActiveFirmCase } = useFirmMode();

  const [me, setMe] = useState(null);
  const [cases, setCases] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);
  const [loadingCases, setLoadingCases] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);

  const token = getFirmToken();

  const fetchWithAuth = useCallback(async (path) => {
    const t = getFirmToken();
    if (!t) return { ok: false, status: 401, data: { error: 'No token' } };
    const res = await fetch(buildApiUrl(path), {
      headers: { Authorization: `Bearer ${t}` },
    });
    let data;
    try {
      data = await res.json();
    } catch {
      data = { error: 'Invalid response' };
    }
    return { ok: res.ok, status: res.status, data };
  }, [getFirmToken]);

  useEffect(() => {
    if (!token) {
      setLoadingMe(false);
      setLoadingCases(false);
      setError({ status: 401, message: 'Firm session missing or expired.' });
      return;
    }

    let cancelled = false;

    (async () => {
      setError(null);
      const [meRes, casesRes] = await Promise.all([
        fetchWithAuth(API_ENDPOINTS.FIRM_ME),
        fetchWithAuth(API_ENDPOINTS.FIRM_CASES),
      ]);

      if (cancelled) return;

      if (!meRes.ok) {
        setMe(null);
        setError({
          status: meRes.status,
          message: meRes.data?.error || `Request failed (${meRes.status})`,
          code: meRes.data?.code,
        });
      } else {
        setMe(meRes.data);
      }

      if (!casesRes.ok && !cancelled) {
        setCases([]);
        if (meRes.ok) setError({
          status: casesRes.status,
          message: casesRes.data?.error || `Failed to load cases (${casesRes.status})`,
          code: casesRes.data?.code,
        });
      } else if (!cancelled) {
        setCases(Array.isArray(casesRes.data) ? casesRes.data : []);
      }

      setLoadingMe(false);
      setLoadingCases(false);
    })();

    return () => { cancelled = true; };
  }, [token, fetchWithAuth]);

  const handleSelectCase = useCallback(async (caseId) => {
    setSelectedCase(null);
    setDocuments(null);
    setDetailError(null);
    if (!caseId) return;

    const t = getFirmToken();
    if (!t) {
      setDetailError({ status: 401, message: 'Firm session expired.' });
      return;
    }

    setLoadingDetail(true);
    const [detailRes, docsRes] = await Promise.all([
      fetchWithAuth(API_ENDPOINTS.FIRM_CASE_BY_ID(caseId)),
      fetchWithAuth(`${API_ENDPOINTS.FIRM_DOCUMENTS}?caseId=${encodeURIComponent(caseId)}`),
    ]);

    if (!detailRes.ok) {
      setDetailError({
        status: detailRes.status,
        message: detailRes.data?.error || `Failed to load case (${detailRes.status})`,
        code: detailRes.data?.code,
      });
      setLoadingDetail(false);
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[FirmCases] raw case detail from GET /api/firm/cases/:id', detailRes.data);
      console.log('[FirmCases] raw documents from GET /api/firm/documents', docsRes.data);
    }

    setSelectedCase(detailRes.data);
    const docList = docsRes.ok && Array.isArray(docsRes.data) ? docsRes.data : [];
    setDocuments(docList);
    setActiveFirmCase(detailRes.data, docList);
    if (!docsRes.ok) setDetailError({ status: docsRes.status, message: 'Could not load documents.' });
    setLoadingDetail(false);
  }, [getFirmToken, fetchWithAuth]);

  const handleClearFirmMode = () => {
    clearFirmMode();
    setMe(null);
    setCases(null);
    setSelectedCase(null);
    setDocuments(null);
    setError(null);
    setDetailError(null);
    window.location.href = '/home';
  };

  const isAuthError = error && (error.status === 401 || error.status === 403);
  const isServerError = error && error.status === 502;

  return (
    <div className="firm-cases-page">
      <div className="firm-cases-page-header">
        <h1>Case Archive (Firm)</h1>
        {me && (
          <div className="firm-cases-user-info">
            <span className="firm-cases-user-email">{me.email}</span>
            {me.roleCodes && me.roleCodes.length > 0 && (
              <span className="firm-cases-user-roles">{me.roleCodes.join(', ')}</span>
            )}
          </div>
        )}
      </div>

      {(loadingMe || loadingCases) && (
        <div className="firm-cases-state firm-cases-loading">
          <p>Loading firm data...</p>
        </div>
      )}

      {error && !loadingMe && !loadingCases && (
        <div className={`firm-cases-state firm-cases-error ${isAuthError ? 'firm-cases-error-auth' : ''}`}>
          <p><strong>Error {error.status}</strong>: {error.message}</p>
          {error.code && <p className="firm-cases-error-code">Code: {error.code}</p>}
          {(isAuthError || isServerError) && (
            <p className="firm-cases-expired-msg">Firm mode may have expired. Clear firm session and sign in again from your firm (Arsheef) if needed.</p>
          )}
          <button type="button" className="firm-cases-btn firm-cases-btn-clear" onClick={handleClearFirmMode}>
            Clear firm mode and go home
          </button>
        </div>
      )}

      {!error && !loadingMe && !loadingCases && me && (
        <>
          <section className="firm-cases-list-section">
            <h2>Cases</h2>
            {cases && cases.length === 0 && (
              <div className="firm-cases-state firm-cases-empty">
                <p>No cases found.</p>
              </div>
            )}
            {cases && cases.length > 0 && (
              <ul className="firm-cases-list">
                {cases.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={`firm-cases-list-item ${selectedCase?.id === c.id ? 'selected' : ''}`}
                      onClick={() => handleSelectCase(c.id)}
                    >
                      <span className="firm-cases-list-title">{c.title || 'Untitled'}</span>
                      <span className="firm-cases-list-meta">
                        {c.caseNumber && `#${c.caseNumber}`} {c.status && ` · ${c.status}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {selectedCase && (
            <section className="firm-cases-detail-section">
              <h2>Case detail</h2>
              {loadingDetail && <div className="firm-cases-state firm-cases-loading"><p>Loading...</p></div>}
              {detailError && !loadingDetail && (
                <div className="firm-cases-state firm-cases-error">
                  <p><strong>Error {detailError.status}</strong>: {detailError.message}</p>
                  {detailError.status === 404 && <p>Case not found or you do not have access.</p>}
                </div>
              )}
              {!loadingDetail && selectedCase && !detailError && (
                <div className="firm-cases-detail-card">
                  <p><strong>Title:</strong> {selectedCase.title}</p>
                  {selectedCase.caseNumber && <p><strong>Case number:</strong> {selectedCase.caseNumber}</p>}
                  {selectedCase.fileNumber && <p><strong>File number:</strong> {selectedCase.fileNumber}</p>}
                  {selectedCase.status && <p><strong>Status:</strong> {selectedCase.status}</p>}
                  {selectedCase.court && <p><strong>Court:</strong> {selectedCase.court}</p>}
                  {selectedCase.city && <p><strong>City:</strong> {selectedCase.city}</p>}
                  {selectedCase.shortDescription && (
                    <p><strong>Description:</strong> {selectedCase.shortDescription}</p>
                  )}
                  {selectedCase.updatedAt && (
                    <p><strong>Updated:</strong> {new Date(selectedCase.updatedAt).toLocaleString()}</p>
                  )}
                </div>
              )}

              {selectedCase && documents && (
                <div className="firm-cases-documents">
                  <h3>Documents</h3>
                  {documents.length === 0 && (
                    <div className="firm-cases-state firm-cases-empty">
                      <p>No documents for this case.</p>
                    </div>
                  )}
                  {documents.length > 0 && (
                    <ul className="firm-cases-doc-list">
                      {documents.map((d) => (
                        <li key={d.id} className="firm-cases-doc-item">
                          <span className="firm-cases-doc-name">{d.name}</span>
                          <span className="firm-cases-doc-meta">
                            {d.documentType} · {d.mimeType} · {(d.fileSize / 1024).toFixed(1)} KB
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default FirmCasesPage;
